/**
 * Emma OS GENERATE productization: the ONE place browser requests are
 * made. `credentials: "include"` sends the HttpOnly session cookie
 * automatically — this code never reads or holds the session token
 * itself (it can't; HttpOnly cookies are invisible to JS by design).
 * State-changing requests attach the CSRF header from the in-memory
 * token React holds (never persisted to localStorage/sessionStorage —
 * see AuthContext's own doc comment for why that's fine: it is reissued
 * on every /auth/me or /auth/login response).
 */
const CSRF_HEADER_NAME = "x-emma-csrf";

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly body: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(method: string, path: string, csrfToken: string | null, body?: unknown): Promise<T> {
  const isStateChanging = method === "POST" || method === "PUT" || method === "PATCH" || method === "DELETE";
  const headers: Record<string, string> = {};
  if (body !== undefined) {
    headers["content-type"] = "application/json";
  }
  if (isStateChanging && csrfToken !== null) {
    headers[CSRF_HEADER_NAME] = csrfToken;
  }
  const response = await fetch(path, {
    method,
    credentials: "include",
    headers,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  const contentType = response.headers.get("content-type") ?? "";
  const parsed = contentType.includes("application/json") ? await response.json().catch(() => null) : null;
  if (!response.ok) {
    throw new ApiError((parsed as { error?: string } | null)?.error ?? `request failed (${response.status})`, response.status, parsed);
  }
  return parsed as T;
}

/** Phase C (persona onboarding): raw-bytes upload (a reference photo) — never JSON.stringify'd, unlike every other mutating call in this client. Same CSRF/credentials discipline as `request()`. */
async function uploadBinary<T>(path: string, csrfToken: string | null, file: File): Promise<T> {
  const headers: Record<string, string> = { "content-type": file.type };
  if (csrfToken !== null) {
    headers[CSRF_HEADER_NAME] = csrfToken;
  }
  const response = await fetch(path, { method: "POST", credentials: "include", headers, body: file });
  const contentType = response.headers.get("content-type") ?? "";
  const parsed = contentType.includes("application/json") ? await response.json().catch(() => null) : null;
  if (!response.ok) {
    throw new ApiError((parsed as { error?: string } | null)?.error ?? `request failed (${response.status})`, response.status, parsed);
  }
  return parsed as T;
}

export const api = {
  get: <T>(path: string, csrfToken: string | null = null) => request<T>("GET", path, csrfToken),
  post: <T>(path: string, csrfToken: string | null, body?: unknown) => request<T>("POST", path, csrfToken, body),
  patch: <T>(path: string, csrfToken: string | null, body?: unknown) => request<T>("PATCH", path, csrfToken, body),
  del: <T>(path: string, csrfToken: string | null) => request<T>("DELETE", path, csrfToken),
  uploadBinary,
};
